import {
  toWSDateString,
  useWSModalCustom,
  WSAlert,
  WSInfoBanner,
  WSInfoBox,
  WSList,
  WSLoader,
  WSMessageBox,
  WSModal,
  WSModalCustomProps,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  IVersionHistoryItem,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces/requirement";
import React from "react";

import { useQueryPayeeRowsSummary } from "../../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { PayeeRowsQuery } from "../../../../services/search";
import { useQueryRequirementDefinition } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinition";
import { WSQueries } from "../../../../query/WSQuery";

type VersionHistoryRow = IVersionHistoryItem & {
  isLatestVersion: boolean;
  requirementDefinitionId: string;
};

type Props = {
  onViewDetails?: () => void;
  requirementDefinitionId: string;
} & WSModalCustomProps<void>;

const ContractorsCell: React.FC<{
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
}> = ({ isLatestVersion, requirementDefinitionId, version }) => {
  const baseFilter: PayeeRowsQuery["filter"] = {
    "requirements.searchableRequirementDefinitionVersion": `${requirementDefinitionId}_${version}`
  };

  const queryTotal = useQueryPayeeRowsSummary({ filter: baseFilter });

  const queryIncomplete = useQueryPayeeRowsSummary({
    filter: {
      ...baseFilter,
      "requirements.status": RequirementStatus.Incomplete
    }
  });

  if (queryTotal.isLoading || queryIncomplete.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const total = queryTotal.data?.listSize ?? 0;
  const incomplete = queryIncomplete.data?.listSize ?? 0;
  const complete = total - incomplete;

  return (
    <WSTableCell
      text={
        <WSList gap="XS">
          <WSText.ParagraphSm>{complete} complete</WSText.ParagraphSm>
          <WSText.ParagraphSm>{incomplete} incomplete</WSText.ParagraphSm>
          {isLatestVersion ? (
            <WSText.ParagraphSm color="gray500">
              + new assignments
            </WSText.ParagraphSm>
          ) : null}
        </WSList>
      }
    />
  );
};

const TABLE_COLUMNS: WSTableColumn<VersionHistoryRow>[] = [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Versions" }
    },
    renderFunction: ({ data }) => {
      return (
        <WSTableCell
          secondaryText={toWSDateString(data.versionCreatedAt, "monthDayYear")}
          secondaryTextColor="gray400"
          text={
            <WSList gap="XS">
              <WSText.ParagraphSm>{`v${data.version}`}</WSText.ParagraphSm>
              {data.versionNote && (
                <WSText.ParagraphXs>
                  Note: {data.versionNote}
                </WSText.ParagraphXs>
              )}
            </WSList>
          }
        />
      );
    }
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Contractors impacted" }
    },
    renderFunction: ({ data }) => (
      <ContractorsCell
        isLatestVersion={data.isLatestVersion}
        requirementDefinitionId={data.requirementDefinitionId}
        version={data.version}
      />
    )
  }
];

const ModalVersionQuickReference: React.FC<Props> = ({
  onViewDetails,
  requirementDefinitionId,
  ...modalProps
}) => {
  const queryRequirementDefinition = useQueryRequirementDefinition(
    requirementDefinitionId
  );

  return (
    <WSModal
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Got it",
            onClick: () => modalProps.onClose()
          },
          ...(onViewDetails
            ? [
                {
                  kind: "Primary" as const,
                  label: "View details",
                  onClick: () => {
                    onViewDetails();
                    modalProps.onClose();
                  }
                }
              ]
            : [])
        ]
      }}
      size="M"
      title="Version quick reference"
      {...modalProps}
    >
      <WSQueries queries={{ queryRequirementDefinition }}>
        {({ queryRequirementDefinitionData: requirementDefinition }) => {
          const versionHistory = requirementDefinition.versionHistory ?? [];
          const latestVersion = requirementDefinition.version;

          const currentVersionEntry: IVersionHistoryItem = {
            minVersion: requirementDefinition.minVersion ?? latestVersion,
            version: latestVersion,
            versionCreatedAt:
              requirementDefinition.versionCreatedAt ??
              requirementDefinition.createdAt,
            versionScope: requirementDefinition.versionScope,
            versionNote: requirementDefinition.versionNote
          };

          const allVersions = [currentVersionEntry, ...versionHistory];

          const tableData = allVersions
            .sort((a, b) => b.version - a.version)
            .map(v => ({
              data: {
                ...v,
                isLatestVersion: v.version === latestVersion,
                requirementDefinitionId:
                  requirementDefinition.requirementDefinitionId
              },
              id: String(v.version)
            }));

          console.log(tableData);

          return (
            <WSList gap="XL">
              <WSAlert
                icon="info-circle"
                size="M"
                theme="info"
                title="About version quick reference"
              >
                A snapshot of currently active versions. To see full version
                details, go to "View details"
              </WSAlert>

              <WSTable<VersionHistoryRow>
                columns={TABLE_COLUMNS}
                showVerticalDivider
                tableData={tableData}
              />
            </WSList>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};

export const useVersionQuickReferenceModal = () =>
  useWSModalCustom(ModalVersionQuickReference);
